import './chatView.scss';
import { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import clsx from 'clsx';
import { useRoomContext } from '@livekit/components-react';
import { useUserStore } from '@/stores/user';
import { useMeetingStore } from '@/stores/meeting';
import { useMessagesStore } from '@/stores/messages';
import { GetMessages } from '@/types/api.ts';
import { ChatMessage } from '@/types/messages';
import api from '@/api/api';

function ChatView() {
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<string>('');

  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef(false);
  const isArrowVisibleRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const lastInputHeightRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const currentScrollRef = useRef(0);
  const isInitialRenderRef = useRef(true);
  const { lastScrollPosition, setScrollPosition } = useMessagesStore();
  const messages = useMessagesStore((state) => state.messages);
  const { resetUnread } = useMessagesStore();
  const { unreadCount } = useMessagesStore();
  const { hasFetchedHistory, setHasFetchedHistory } = useMessagesStore();
  const { addMessage, updateMessageStatus, prependMessages, setMessages } = useMessagesStore();

  const room = useRoomContext();
  const userName = useUserStore((state) => state.userName);
  const meetingTitle = useMeetingStore((state) => state.meeting?.title) || '';
  const meetingUuid = useMeetingStore((state) => state.meeting?.uuid);

  const formatDate = (date: Date) =>
    date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long'
    });

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });

  useLayoutEffect(() => {
    if (!meetingUuid) return;

    loadInitialMessages();
  }, [meetingUuid]);

  const PAGE_SIZE = 30;

  const mapApiMessageToChatMessage = (msg: GetMessages, currentUserId: string): ChatMessage => ({
    id: msg.uuid,
    author: msg.author_name,
    authorId: msg.author_uuid,
    text: msg.text,
    fromMe: msg.author_uuid === currentUserId,
    createdAt: new Date(msg.created_at),
    meetingTitle: msg.meeting_title,
    status: 'sent'
  });

  async function loadInitialMessages() {
    if (!meetingUuid || hasFetchedHistory) return;

    autoScrollRef.current = true;
    setIsLoading(true);

    const data = await api.getMessages({
      meeting_uuid: meetingUuid,
      limit: PAGE_SIZE
    });
    if (data.length !== 0) {
      setMessages(
        data.map((msg) => mapApiMessageToChatMessage(msg, room.localParticipant.identity))
      );
      setHasMore(data.length === PAGE_SIZE);
    }
    setHasFetchedHistory(true);

    setIsLoading(false);
  }

  const sendMessage = async () => {
    if (!value.trim()) return;

    autoScrollRef.current = true;
    isArrowVisibleRef.current = false;

    const msg: ChatMessage = {
      id: crypto.randomUUID(),
      author: userName,
      authorId: room.localParticipant.identity,
      text: value,
      fromMe: true,
      createdAt: new Date(),
      meetingTitle,
      status: 'sending'
    };

    addMessage(msg);

    try {
      await Promise.all([
        api.sendMessage({
          uuid: msg.id,
          meeting_uuid: meetingUuid,
          meeting_title: msg.meetingTitle,
          author_uuid: msg.authorId,
          author_name: msg.author,
          text: msg.text,
          created_at: msg.createdAt.toISOString()
        }),
        room.localParticipant.sendText(msg.text, {
          topic: meetingTitle,
          attributes: {
            messageId: msg.id,
            meetingTitle: msg.meetingTitle,
            author: msg.author,
            authorId: msg.authorId
          }
        })
      ]);

      updateMessageStatus(msg.id, 'sent');
    } catch (error) {
      console.error('Ошибка при отправке сообщения', error);
      updateMessageStatus(msg.id, 'error');
    }

    lastInputHeightRef.current = 0;

    setValue('');
  };

  async function loadOlderMessages() {
    const el = messagesRef.current;
    if (isLoading || !meetingUuid || !el) return;

    setIsLoading(true);

    const firstMessage = messages[0];
    const data = await api.getMessages({
      meeting_uuid: meetingUuid,
      limit: PAGE_SIZE,
      before: firstMessage.createdAt.toISOString()
    });

    if (data.length === 0) {
      prevScrollHeightRef.current = 0;
    } else {
      prevScrollHeightRef.current = el.scrollHeight;
    }

    const mapped = data.map((msg) =>
      mapApiMessageToChatMessage(msg, room.localParticipant.identity)
    );

    prependMessages(mapped);

    setHasMore(mapped.length === PAGE_SIZE);

    setIsLoading(false);
  }

  const TOP_THRESHOLD = 50;
  const BOTTOM_THRESHOLD = 50;

  const checkIfAtBottom = () => {
    const el = messagesRef.current;
    if (!el || messages.length === 0) return true;

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    return distanceToBottom < BOTTOM_THRESHOLD + lastInputHeightRef.current;
  };

  const updateArrowVisibility = () => {
    const atBottom = checkIfAtBottom();

    const shouldShow = !atBottom;

    if (isArrowVisibleRef.current !== shouldShow) {
      isArrowVisibleRef.current = shouldShow;
      if (showScrollDown !== !atBottom) {
        setShowScrollDown(!atBottom);
      }
    }

    autoScrollRef.current = atBottom;
  };

  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;

    currentScrollRef.current = el.scrollTop;
    const atBottom = checkIfAtBottom();
    autoScrollRef.current = atBottom;
    if (atBottom) {
      resetUnread();
    }

    if (el.scrollTop < TOP_THRESHOLD && !isLoading && hasMore) {
      loadOlderMessages();
    }

    updateArrowVisibility();
  };

  useLayoutEffect(() => {
    const atBottom = checkIfAtBottom();
    if (autoScrollRef.current || atBottom) {
      resetUnread();
    }
  }, [unreadCount]);

  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!autoScrollRef.current || !el) return;
    el.scrollTop = el.scrollHeight;

    setShowScrollDown(false);
  }, [messages]);

  useEffect(() => {
    const el = messagesRef.current;
    return () => {
      if (autoScrollRef.current) {
        setScrollPosition(-10);
      } else if (el && messages.length !== 0) {
        setScrollPosition(currentScrollRef.current);
      }
    };
  }, [messages]);

  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || isLoading) return;

    if (isInitialRenderRef.current) {
      if (lastScrollPosition < 0) {
        el.scrollTop = el.scrollHeight;
      } else {
        el.scrollTop = lastScrollPosition;
      }
      updateArrowVisibility();
      autoScrollRef.current = false;
      isInitialRenderRef.current = false;
      return;
    }

    const diff = el.scrollHeight - prevScrollHeightRef.current;

    if (isLoading && el.scrollTop === 0 && diff > 0 && prevScrollHeightRef.current > 0) {
      el.scrollTop = diff;
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    autoScrollRef.current = true;
    isArrowVisibleRef.current = false;
  };

  const handleInputResize = () => {
    const container = messagesRef.current;
    const footer = footerRef.current;
    if (!container || !footer) return;

    requestAnimationFrame(() => {
      const newHeight = footer.offsetHeight;
      const diff = newHeight - lastInputHeightRef.current;
      const atBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        BOTTOM_THRESHOLD + footer.offsetHeight;
      if (lastInputHeightRef.current !== 0 && !atBottom) {
        container.scrollTop += diff;
      } else if (atBottom) {
        container.scrollTop = container.scrollHeight;
      }
      lastInputHeightRef.current = newHeight;
    });
  };

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
    [messages]
  );

  return (
    <div className="chat-view">
      <div className="chat-view__messages" ref={messagesRef} onScroll={handleScroll}>
        <div className="messages-spacer" />
        {sortedMessages.map((msg, index) => {
          const prev = messages[index - 1];
          const next = messages[index + 1];

          const showMeetingTitle = !prev || prev.meetingTitle !== msg.meetingTitle;

          const showDate = !prev || prev.createdAt.toDateString() !== msg.createdAt.toDateString();

          const isLastInSequence = !next || next.authorId !== msg.authorId;

          return (
            <div key={msg.id}>
              {showDate && (
                <div className="date-separator">
                  <span className="date-separator__label">{formatDate(msg.createdAt)}</span>
                </div>
              )}

              {showMeetingTitle && (
                <div className="topic-separator">
                  <span className="topic-separator__label">{msg.meetingTitle}</span>
                </div>
              )}

              <div className={clsx('message', msg.fromMe ? 'message--me' : 'message--other')}>
                <div className={clsx('message__bubble', isLastInSequence && 'message--with-tail')}>
                  {!msg.fromMe && <div className="message__author">{msg.author}</div>}
                  {msg.text}
                  <span className="message__time">{formatTime(msg.createdAt)}</span>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-footer" ref={footerRef}>
        {showScrollDown && (
          <Button icon="pi pi-arrow-down" className="scroll-down-btn" onClick={scrollToBottom} />
        )}
        <div className="chat-view__input">
          <InputTextarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={1}
            autoResize
            placeholder="Введите сообщение..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            onInput={handleInputResize}
          />
          <Button icon="pi pi-send" className="send-btn" onClick={sendMessage} />
        </div>
      </div>
    </div>
  );
}

export default ChatView;
